import { List } from 'immutable';
import {
    ChatMessageKey,
    ChatSessionKey,
    ExtensionKey,
} from 'flow/types/EntityKeys';
import { Message } from 'flow/types/Chats';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatContent } from 'flow/utils/message-utils';

export const selectChatMessages = (
    state: State,
    chatSessionKey: ChatSessionKey,
): List<TypedMap<Message>> | undefined =>
    state.chats.getIn(['messages', chatSessionKey]);

/* The three content lists below are all oldest first, as stored. Reverse at the call site,
 * memoised on this: a reversed List is a new object every call, which no reference check
 * can hold. */
export const selectSessionPosts = (
    state: State,
    chatSessionKey: ChatSessionKey,
) => state.chats.getIn(['content', ChatContent.POSTS, chatSessionKey], List());

export const selectSessionFiles = (
    state: State,
    chatSessionKey: ChatSessionKey,
) => state.chats.getIn(['content', ChatContent.FILES, chatSessionKey], List());

export const selectSessionImages = (
    state: State,
    chatSessionKey: ChatSessionKey,
) => state.chats.getIn(['content', ChatContent.IMAGES, chatSessionKey], List());

export const selectFailedMessages = (
    state: State,
    chatSessionKey: ChatSessionKey,
): List<TypedMap<Message>> =>
    state.chats.getIn(['failedMessages', chatSessionKey], List());

/* One row asking "which message am I?" used to scan the whole list, so a chat
 * with N messages did N scans of N on every dispatched action - 251 messages
 * measured at 2.6ms per action, against 0.09ms at 50. Index the list instead,
 * keyed on the list itself: mergeNewChatMessageList hands back the same List
 * when nothing changed, so the index is rebuilt only when the messages really
 * do change, and every row's lookup in between is a map get. */
const messageIndexCache = new WeakMap<
    object,
    Map<ChatMessageKey, TypedMap<Message>>
>();

const indexFor = (
    messages: List<TypedMap<Message>>,
): Map<ChatMessageKey, TypedMap<Message>> => {
    const cached = messageIndexCache.get(messages);
    if (cached) return cached;

    const index = new Map<ChatMessageKey, TypedMap<Message>>();
    messages.forEach((message) => {
        // First occurrence wins, because the scan this replaces returned the
        // first match. Both write paths dedupe by key, so a repeat should not
        // arise - this only makes sure that if one ever does, the answer does
        // not quietly change.
        const key = message.get('key');
        if (!index.has(key)) index.set(key, message);
    });
    messageIndexCache.set(messages, index);
    return index;
};

export const selectChatMessageByKey = (
    state: State,
    chatSessionKey: ChatSessionKey,
    chatMessageKey: ChatMessageKey,
): TypedMap<Message> | undefined => {
    const messages: List<TypedMap<Message>> | undefined = state.chats.getIn([
        'messages',
        chatSessionKey,
    ]);
    if (!messages) return undefined;
    return indexFor(messages).get(chatMessageKey);
};

export const selectFailedMessageByKey = (
    state: State,
    chatSessionKey: ChatSessionKey,
    chatMessageKey: ChatMessageKey,
): TypedMap<Message> | undefined => {
    const messages: List<TypedMap<Message>> = state.chats.getIn(
        ['failedMessages', chatSessionKey],
        List(),
    );
    return indexFor(messages).get(chatMessageKey);
};

export const selectMessagesExist = (
    state: State,
    chatSessionKey: ChatSessionKey,
): boolean => !!state.chats.getIn(['messages', chatSessionKey]);

export const selectLastMessageKey = (
    state: State,
    chatSessionKey: ChatSessionKey,
): ChatMessageKey | undefined => {
    const messages: List<TypedMap<Message>> | undefined = state.chats.getIn([
        'messages',
        chatSessionKey,
    ]);
    if (!messages || messages.size === 0) return undefined;
    return messages.last().get('key');
};

export const selectLastMessageRead = (
    state: State,
    chatSessionKey: ChatSessionKey,
): boolean => {
    const messages: List<TypedMap<Message>> | undefined = state.chats.getIn([
        'messages',
        chatSessionKey,
    ]);
    if (!messages || messages.size === 0) return true;
    return messages.last().get('read');
};

/** A message pushed over Top is assembled client-side from the notification
 *  and carries less than the REST representation - notably a quotedMessage in
 *  a different shape than the one a message row renders - so a list whose tail
 *  came from Top has to be refetched before it can be shown in full. */
export const selectLastMessageIsFromTop = (
    state: State,
    chatSessionKey: ChatSessionKey,
): boolean => {
    const messages: List<TypedMap<Message>> | undefined = state.chats.getIn([
        'messages',
        chatSessionKey,
    ]);
    if (!messages || messages.size === 0) return false;
    return messages.last().get('source') === 'Top';
};

/** True when the newest message is one you sent yourself. A message of your own
 *  should bring the view down to it however far back you were reading, which is
 *  not true of anybody else's. */
export const selectLastMessageIsOwn = (
    state: State,
    chatSessionKey: ChatSessionKey,
    ownExtensionKey: ExtensionKey,
): boolean => {
    const messages: List<TypedMap<Message>> | undefined = state.chats.getIn([
        'messages',
        chatSessionKey,
    ]);
    if (!messages || messages.size === 0) return false;
    return messages.last().getIn(['from', 'extensionKey']) === ownExtensionKey;
};

export const selectHasReadAllMessages = (
    state: State,
    chatSessionKey: ChatSessionKey,
): boolean =>
    state.chats.getIn([
        'sessions',
        chatSessionKey,
        'numberOfUnreadMessages',
    ]) === 0;

export const selectMessageReactions = (
    state: State,
    chatSessionKey: ChatSessionKey,
    chatMessageKey: ChatMessageKey,
) => state.chats.getIn(['reactions', chatSessionKey, chatMessageKey], List());

export const findFirstUnreadMessageKey = (
    messages: List<TypedMap<Message>> | undefined,
    ownExtensionKey: ExtensionKey,
): ChatMessageKey | undefined => {
    if (!messages || messages.size === 0) return undefined;

    const lastMessage = messages.last();
    if (lastMessage.getIn(['from', 'extensionKey']) === ownExtensionKey)
        return undefined;

    let firstKey: ChatMessageKey | undefined;
    for (let i = messages.size - 1; i >= 0; i--) {
        if (messages.get(i).get('read') !== false) break;
        firstKey = messages.get(i).get('key');
    }
    return firstKey;
};
